import {
  cataloguePriceFailure,
  deriveCataloguePrice,
} from '~/lib/catalogue-price'
import { compareClassicGridOrder } from '~/lib/classic-sorting'
import { invalidSaleReporter, logNonFatal } from '~/lib/diagnostics'
import type { ItemType, ShopBootstrap, ShopProduct } from './bootstrap'
import {
  deriveCardContent,
  deriveOwnedGids,
  type OwnedGids,
} from './card-candidate'
import type {
  CatalogueCandidate,
  CatalogueCandidateDraft,
  CatalogueIdentity,
} from './catalogue'

// The Classic collection (GLOSSARY.md "Classic collection"): the shop-classic
// tree species and land skins rendered on the Classic tab, one content kind
// at a time behind the Classic content filter (ADR 0033). This module owns
// its derivation; hydration is the shared catalogue path.

export type ClassicSource = Pick<
  ShopBootstrap,
  'collections' | 'itemTypes' | 'unlockedLandSkins' | 'unlockedTrees'
>

type ClassicOmissionReason =
  | 'duplicate_gid'
  | 'invalid_collection_entry'
  | 'invalid_content'
  | 'invalid_price'
  | 'invalid_purchase_type'
  | 'missing_product'
  | 'missing_required_asset'

function reportOmission(
  reason: ClassicOmissionReason,
  productIds: number[],
  identity?: CatalogueIdentity,
): void {
  logNonFatal('shop_classic_product_omitted', {
    reason,
    product_ids: productIds.join(','),
    ...(identity === undefined
      ? {}
      : { content: identity.content, gid: String(identity.gid) }),
  })
}

function deriveCandidate(
  product: ShopProduct,
  itemType: ItemType | undefined,
  owned: OwnedGids,
): CatalogueCandidateDraft | null {
  const result = deriveCardContent(product, itemType)
  if ('failure' in result) {
    reportOmission(result.failure, [product.id], result.identity)
    return null
  }
  const { card } = result
  return { ...card, owned: owned[card.content].has(card.gid), product }
}

function validateCandidate(candidate: CatalogueCandidateDraft): boolean {
  const { product } = candidate
  const priceFailure = cataloguePriceFailure(product)
  if (priceFailure) {
    reportOmission(priceFailure, [product.id], candidate)
    return false
  }
  if (!candidate.owned && product.purchaseable !== true) return false
  return true
}

const identityKey = (candidate: CatalogueIdentity) =>
  `${candidate.content}:${candidate.gid}`

// The grid's pre-hydration facts: identity, price, and ownership per
// product, in grid order — every tree, then every land skin, each kind free
// products first, then gid descending. Every sort key is known here, so the
// slot order is fixed before any native call resolves.
export function deriveClassicCandidates(
  source: ClassicSource,
): CatalogueCandidate[] {
  const itemTypes = new Map<number, ItemType>()
  for (const itemType of source.itemTypes) {
    if (!itemType || typeof itemType !== 'object') continue
    itemTypes.set(itemType.id, itemType)
  }
  const owned = deriveOwnedGids(source)

  const derived = source.collections[
    'shop-classic'
  ].item_type_product_collection_products.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') {
      reportOmission('invalid_collection_entry', [])
      return []
    }
    const product = entry.item_type_product
    if (!product) {
      reportOmission('missing_product', [
        entry.item_type_product_id ?? entry.id,
      ])
      return []
    }
    const candidate = deriveCandidate(
      product,
      itemTypes.get(product.item_type_id ?? -1),
      owned,
    )
    return candidate ? [candidate] : []
  })

  const byIdentity = new Map<string, CatalogueCandidateDraft[]>()
  for (const candidate of derived) {
    const matches = byIdentity.get(identityKey(candidate)) ?? []
    matches.push(candidate)
    byIdentity.set(identityKey(candidate), matches)
  }
  const unique = derived.filter((candidate) => {
    const duplicates = byIdentity.get(identityKey(candidate))!
    if (duplicates.length === 1) return true
    if (duplicates[0] === candidate) {
      reportOmission(
        'duplicate_gid',
        duplicates.map(({ product }) => product.id),
        candidate,
      )
    }
    return false
  })

  return unique
    .filter(validateCandidate)
    .map((candidate) => ({
      ...candidate,
      price: deriveCataloguePrice(
        candidate.product,
        invalidSaleReporter(candidate.product.id, candidate.gid),
      ),
    }))
    .sort(compareClassicGridOrder)
}
